import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/lib/currency";

const EMPTY_UNIT = { name: "", monthly_rent: 0, tenant_name: "", lease_end: "" };
export const INITIAL_FORM = {
  name: "", address: "", description: "", image_url: "",
  units: [{ ...EMPTY_UNIT, name: "Unit 1" }],
};

const aggregateRent = (units) =>
  (units || []).reduce((s, u) => s + Number(u.monthly_rent || 0), 0);

export default function AddPropertyDialog({ open, onOpenChange, form, setForm, onSubmit }) {
  const { t } = useTranslation();
  const { fmt } = useCurrency();

  const updateUnit = (i, k, v) => {
    const next = [...form.units];
    next[i] = { ...next[i], [k]: v };
    setForm({ ...form, units: next });
  };
  const addUnit = () => setForm({
    ...form,
    units: [...form.units, { ...EMPTY_UNIT, name: `Unit ${form.units.length + 1}` }],
  });
  const removeUnit = (i) => setForm({
    ...form,
    units: form.units.filter((_, idx) => idx !== i),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="add-property-btn" className="rounded-none bg-black hover:bg-zinc-800 font-mono-label text-xs h-11">
          + {t("ll_add_facility")}
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{t("ll_add_facility")}</DialogTitle></DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <Label className="font-mono-label text-[10px]">{t("ll_facility_name")}</Label>
          <Input data-testid="prop-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
          <Label className="font-mono-label text-[10px]">{t("ll_address")}</Label>
          <Input data-testid="prop-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none" />
          <Label className="font-mono-label text-[10px]">{t("ll_image_url")}</Label>
          <Input data-testid="prop-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="rounded-none" />
          <Label className="font-mono-label text-[10px]">{t("ll_description")}</Label>
          <Textarea data-testid="prop-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" />

          <div className="border border-black/10 p-3 bg-zinc-50 mt-2">
            <div className="flex justify-between items-center mb-3">
              <div>
                <div className="font-mono-label text-[10px] text-[#002FA7]">⎯⎯ {t("ll_units_header")}</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {t("ll_aggregate_rent")}: <b className="tabular-nums">{fmt(aggregateRent(form.units))}</b>
                </div>
              </div>
              <Button type="button" data-testid="add-unit" onClick={addUnit} variant="outline" className="rounded-none border-black h-8 font-mono-label text-[10px]">
                {t("ll_add_unit")}
              </Button>
            </div>
            <div className="space-y-2">
              {form.units.map((u, i) => (
                <div key={`unit-${i}-${u.name}`} className="grid grid-cols-12 gap-2 items-center">
                  <Input placeholder={t("ll_unit_name")} value={u.name} onChange={(e) => updateUnit(i, "name", e.target.value)} className="rounded-none col-span-3" required />
                  <Input placeholder={t("ll_unit_rent")} type="number" value={u.monthly_rent} onChange={(e) => updateUnit(i, "monthly_rent", e.target.value)} className="rounded-none col-span-3" required min={0} />
                  <Input placeholder={t("ll_unit_tenant")} value={u.tenant_name} onChange={(e) => updateUnit(i, "tenant_name", e.target.value)} className="rounded-none col-span-3" />
                  <Input placeholder={t("ll_unit_lease_end")} value={u.lease_end} onChange={(e) => updateUnit(i, "lease_end", e.target.value)} className="rounded-none col-span-2" />
                  <button type="button" onClick={() => removeUnit(i)} className="col-span-1 text-xs text-red-600 font-mono-label">×</button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button data-testid="prop-submit" type="submit" className="rounded-none bg-[#002FA7] hover:bg-[#001F70]">
              {t("ll_create_facility")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
